import { Locales } from '@griply/types';
import {
  getAutomaticTimeZoneUpdate,
  getDeviceTimeZone,
  getTimeFormat,
  getUserTimeZone,
} from '@griply/utils';
import {
  useRequestEmailChange,
  useUpdatePassword,
  useUpdateUser,
  useUpdateUserSettings,
} from 'features/user';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChangeEmailDialog } from 'shared/dialogs/change-email';
import { ChangePasswordDialog } from 'shared/dialogs/change-password';
import { UserSettingsDialog } from 'shared/dialogs/user-settings';
import { SettingsSection } from 'shared/dialogs/user-settings/settings-nav';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { useToggle } from 'shared/hooks/use-toggle';
import { useUser } from 'shared/hooks/use-user';
import {
  DateFormatOptions,
  initialDateFormat,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import {
  initialNumberFormat,
  NumberFormatOptions,
} from 'shared/types/number-format-options';
import { initialThemeMode, ThemeModes } from 'shared/types/theme-modes';
import { initialWeekStartsOn, WeekDays } from 'shared/types/week-days';
import { targetIsInput } from 'shared/utils/target-is-input';

import { SettingsOpenOrigin } from './settings-open-origin';
import {
  UserSettingsContext,
  UserSettingsContextProps,
} from './user-settings-context';

export type UserSettingsProviderProps = {
  children: React.ReactNode;
};

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({
  children,
}) => {
  const [dialogOpen, toggleDialog, forceDialog] = useToggle(false);
  // The form re-mounts on every open (MUI unmounts a closed Dialog's children), so this is
  // read once as its initial activeSection and never has to be pushed down mid-open.
  const [settingsSection, setSettingsSection] = useState<SettingsSection>();
  const [openOrigin, setOpenOrigin] = useState<SettingsOpenOrigin>();
  const [passwordDialogOpen, togglePasswordDialog, forcePasswordDialog] =
    useToggle(false);
  const [emailDialogOpen, , forceEmailDialog] = useToggle(false);
  const user = useUser();
  const { submit: updateUser } = useUpdateUser();
  const updateUserSettings = useUpdateUserSettings();
  const {
    updatePassword,
    isLoading: passwordLoading,
    error: passwordError,
    isSuccess: passwordSuccess,
  } = useUpdatePassword();
  const {
    requestEmailChange,
    reset: resetEmailChange,
    isLoading: emailChangeLoading,
    error: emailChangeError,
    isSuccess: emailChangeSent,
    requestedEmail,
  } = useRequestEmailChange();

  // Each open starts from the form, never from the last request's sent step or error.
  const openChangeEmail = useCallback(() => {
    resetEmailChange();
    forceEmailDialog(true);
  }, [forceEmailDialog, resetEmailChange]);

  const closeChangeEmail = useCallback(
    () => forceEmailDialog(false),
    [forceEmailDialog],
  );

  const [updateUserName] = useDelayedCallback(
    useCallback(
      (name: string) => !!name && updateUser({ name }, user),
      [updateUser, user],
    ),
    1000,
  );

  const updateUserLanguage = useCallback(
    (language: Locales) => updateUser({ language }, user),
    [updateUser, user],
  );

  const updateUserDateFormat = useCallback(
    (dateFormat: DateFormatOptions) => updateUser({ dateFormat }, user),
    [updateUser, user],
  );

  const updateUserNumberFormat = useCallback(
    (numberFormat: NumberFormatOptions) => updateUserSettings({ numberFormat }),
    [updateUserSettings],
  );

  const updateUserTimeFormat = useCallback(
    (timeFormat: TimeFormatOptions) => updateUserSettings({ timeFormat }),
    [updateUserSettings],
  );

  const updateWeekStart = useCallback(
    (weekDay: WeekDays) => updateUser({ startOfWeek: weekDay }, user),
    [updateUser, user],
  );

  const updateUserTimezone = useCallback(
    (timezone: string) => updateUserSettings({ timezone }),
    [updateUserSettings],
  );

  const updateAutomaticTimezone = useCallback(
    (automatic: boolean) =>
      updateUserSettings(
        getAutomaticTimeZoneUpdate(automatic, getDeviceTimeZone()),
      ),
    [updateUserSettings],
  );

  const updateUserThemePreference = useCallback(
    (themePreference: ThemeModes) => updateUser({ themePreference }, user),
    [updateUser, user],
  );

  // Forces open rather than toggling: a targeted open must retarget, never dismiss.
  const openSettings = useCallback(
    (section?: SettingsSection, origin?: SettingsOpenOrigin) => {
      setSettingsSection(section);
      setOpenOrigin(origin);
      forceDialog(true);
    },
    [forceDialog],
  );

  const value = useMemo<UserSettingsContextProps>(
    () => ({
      openSettings,
      openChangePassword: togglePasswordDialog,
      openOrigin,
    }),
    [openSettings, togglePasswordDialog, openOrigin],
  );

  useEffect(() => {
    if (passwordSuccess) {
      forcePasswordDialog(false);
    }
  }, [forcePasswordDialog, passwordSuccess]);

  useModifierKeysDown('Comma', (e) => {
    if (targetIsInput(e.target)) {
      return; // Don't open dialog
    }
    // via openSettings, so the shortcut clears a section a previous targeted open left behind
    openSettings();
    e.preventDefault();
  });

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
      {!!user && (
        <>
          <UserSettingsDialog
            open={dialogOpen}
            onClose={toggleDialog}
            initialSection={settingsSection}
            userName={user.name}
            updateUserName={updateUserName}
            language={user.settings?.language}
            onChangeLanguage={updateUserLanguage}
            email={user.email}
            signInProvider={user.signInProvider}
            onChangeEmail={openChangeEmail}
            dateFormat={user.settings?.dateFormat ?? initialDateFormat}
            onChangeDateFormat={updateUserDateFormat}
            numberFormat={user.settings?.numberFormat ?? initialNumberFormat}
            onChangeNumberFormat={updateUserNumberFormat}
            timeFormat={getTimeFormat(user)}
            onChangeTimeFormat={updateUserTimeFormat}
            weekStartsOn={user.settings?.startOfWeek ?? initialWeekStartsOn}
            onChangeWeekStart={updateWeekStart}
            timezone={getUserTimeZone(user)}
            onChangeTimezone={updateUserTimezone}
            isAutomaticTimezone={!user.settings?.manualTimezone}
            onChangeAutomaticTimezone={updateAutomaticTimezone}
            onChangePassword={togglePasswordDialog}
            themePreference={
              user.settings?.themePreference?.desktop ?? initialThemeMode
            }
            onChangeThemePreference={updateUserThemePreference}
          />
          <ChangeEmailDialog
            open={emailDialogOpen}
            currentEmail={user.email}
            sentTo={emailChangeSent ? requestedEmail : undefined}
            isLoading={emailChangeLoading}
            error={emailChangeError}
            onSubmit={requestEmailChange}
            onClose={closeChangeEmail}
          />
          <ChangePasswordDialog
            open={passwordDialogOpen}
            onSubmit={updatePassword}
            error={passwordError ?? undefined}
            isLoading={passwordLoading}
            onClose={togglePasswordDialog}
          />
        </>
      )}
    </UserSettingsContext.Provider>
  );
};
